import React, { useState, useEffect, useRef } from "react";
import { api } from "./api";
import T from "./i18n";
import themes from "./constants/themes";
import Ctx from "./contexts/AppContext";
import usePullToRefresh from "./hooks/usePullToRefresh";
import { normNotifs } from "./utils/notifications";
import { registerServiceWorker, enablePushNotifications, isPushSupported } from "./utils/pushSubscribe";

// --- Layout Components ---
import LoginPage from "./components/layout/LoginPage";
import TopBar from "./components/layout/TopBar";
import BottomBar from "./components/layout/BottomBar";

// --- Role Page Routers ---
import LandlordPage from "./components/landlord/LandlordPage";
import TenantPage from "./components/tenant/TenantPage";
import ServicePage from "./components/service/ServicePage";
import AdminPage from "./components/admin/AdminPage";
import PublicBrowsePage from "./components/public/PublicBrowsePage";
import FeedbackButton from "./components/common/FeedbackButton";

// --- Shared UI ---
import Noti from "./components/common/Noti";
import ConfirmModal from "./components/common/ConfirmModal";

const initData = {
  props: [{ id: "p1", addr: "12, Gandhi Nagar, Sivakasi", rooms: 2, rent: 8000, dep: 16000, amen: ["Water", "WiFi"], occ: true, tid: "t1" }, { id: "p2", addr: "45, Thiruvalluvar St, Sivakasi", rooms: 3, rent: 12000, dep: 24000, amen: ["AC", "Water", "WiFi"], occ: true, tid: "t2" }, { id: "p3", addr: "78, Meenakshi Nagar, Sivakasi", rooms: 1, rent: 5000, dep: 10000, amen: ["Water"], occ: false, tid: null }, { id: "p4", addr: "23, Collector Office Rd, Sivakasi", rooms: 2, rent: 10000, dep: 20000, amen: ["AC", "Water"], occ: true, tid: "t3" }],
  tens: [{ id: "t1", name: "Murugan K", ph: "+91 98765 43210", aad: "4521", join: "2024-06-15", pid: "p1", vac: false }, { id: "t2", name: "Lakshmi S", ph: "+91 87654 32109", aad: "7832", join: "2024-03-01", pid: "p2", vac: false }, { id: "t3", name: "Ravi M", ph: "+91 76543 21098", aad: "1256", join: "2025-01-10", pid: "p4", vac: true }],
  pays: [{ id: "y1", tid: "t1", amt: 8000, com: 500, dt: "2025-02-01", mth: "upi", st: "paid", mo: "Feb 25" }, { id: "y2", tid: "t1", amt: 8000, com: 450, dt: "2025-01-03", mth: "cash", st: "paid", mo: "Jan 25" }, { id: "y3", tid: "t2", amt: 12000, com: 500, dt: "2025-02-01", mth: "upi", st: "paid", mo: "Feb 25" }, { id: "y4", tid: "t2", amt: 12000, com: 450, dt: "2025-01-05", mth: "bank", st: "paid", mo: "Jan 25" }, { id: "y5", tid: "t3", amt: 10000, com: 500, dt: "", mth: "", st: "due", mo: "Feb 25" }],
  maints: [{ id: "m1", tid: "t1", desc: "Bathroom tap leaking", urg: "high", st: "open", dt: "2025-02-03" }, { id: "m2", tid: "t2", desc: "Fan not working in bedroom", urg: "med", st: "assigned", dt: "2025-01-28" }, { id: "m3", tid: "t1", desc: "Wall paint peeling", urg: "low", st: "done", dt: "2025-01-15" }],
  ccs: [{ id: "c1", mo: "Feb 2025", eb: 3000, cl: 1500, n: 3 }, { id: "c2", mo: "Jan 2025", eb: 2700, cl: 1500, n: 3 }],
  msgs: [{ id: "g1", pid: "p1", fr: "ll", tx: "Rent due on 1st, pay via UPI", tm: "9:00" }, { id: "g2", pid: "p1", fr: "t1", tx: "Paid via UPI, please check", tm: "10:30" }, { id: "g3", pid: "p2", fr: "ll", tx: "Maintenance team visits tomorrow", tm: "14:00" }]
};

export default function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [dk, setDk] = useState(() => localStorage.getItem("dk") === "true");
  const [role, setRole] = useState(() => localStorage.getItem("role") || null);
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem("loggedIn") === "true");
  const [page, setPage] = useState(() => window.history.state?.page || "dash");
  const [publicPage, setPublicPage] = useState(null);
  const [selPropId, setSelPropId] = useState(null);
  const [selTid, setSelTid] = useState(null);
  const [props, setProps] = useState([]);
  const [tens, setTens] = useState([]);
  const [pays, setPays] = useState([]);
  const [mts, setMts] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [notif, setNotif] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [currUserId, setCurrUserId] = useState(() => localStorage.getItem("currUserId") || null);
  const [dbErr, setDbErr] = useState(null);
  const [isSyncing, setIsSyncing] = useState(true);

  const [openChat, setOpenChat] = useState(null);
  const [settings, setSettings] = useState({ upi_id: "", upi_name: "", bank_details: "", admin_password: "", agreement_terms: "", landlord_signature: "" });
  const [editingCC, setEditingCC] = useState(null);
  const [sps, setSps] = useState([]);
  const [units, setUnits] = useState([]);
  const [selMaintId, setSelMaintId] = useState(null);
  const [leases, setLeases] = useState([]);
  const [selLeaseId, setSelLeaseId] = useState(null);
  const [plan, setPlan] = useState({ type: "free", maxProperties: 3, currentCount: 0 });
  const [selOrgId, setSelOrgId] = useState(null);
  const [orgId, setOrgId] = useState(() => localStorage.getItem("orgId") || null);
  const [userName, setUserName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgRole, setOrgRole] = useState("owner");

  // Persist language preference
  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => { localStorage.setItem("dk", dk ? "true" : "false"); const th = dk ? themes.dark : themes.light; document.body.style.background = th.bg; document.body.style.color = th.tx; }, [dk]);

  // Register the service worker once on app boot. Idempotent — safe even
  // if the user isn't logged in yet (the SW just sits idle until a push
  // arrives, and pushes only arrive once the user has subscribed).
  useEffect(() => {
    if (!isPushSupported()) return;
    registerServiceWorker().catch(() => {});
  }, []);

  // After login: if the user has already granted notification permission
  // on this device (i.e. they enabled it before and just came back), make
  // sure we have a fresh subscription registered against the server with
  // the *current* user identity. Without this, a tenant logging in on a
  // browser where a previous tenant subscribed would get the wrong pushes.
  useEffect(() => {
    if (!loggedIn) return;
    if (!isPushSupported()) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Re-subscribe quietly — won't prompt because permission is already granted.
    enablePushNotifications().catch(() => {});
  }, [loggedIn, currUserId]);

  // Initial Data Fetch — re-runs on login/logout
  useEffect(() => {
    // Not logged in — nothing to sync
    if (!loggedIn) { setIsSyncing(false); return; }
    // If marked as logged in but no auth token, force logout immediately
    if (!localStorage.getItem('auth_token')) {
      setLoggedIn(false); setRole(null); setCurrUserId(null); setIsSyncing(false);
      localStorage.removeItem('loggedIn'); localStorage.removeItem('role'); localStorage.removeItem('currUserId');
      return;
    }
    // Admin and social user roles skip sync
    const currentRole = localStorage.getItem('role');
    if (currentRole === 'admin' || currentRole === 'user') { setIsSyncing(false); return; }
    // Show loading spinner for fresh login
    setIsSyncing(true);
    const syncData = async () => {
      try {
        const data = await api.sync();
        if (data) {
          if (data.dbError) {
            setDbErr(data.dbError);
            return;
          }
          setDbErr(null);
          setProps(data.props || []);
          setTens(data.tens || []);
          setPays(data.pays || []);
          setMts(data.maints || []);
          setCcs(data.ccs || []);
          setMsgs(data.msgs || []);
          setNotifs(normNotifs(data.notifs));
          setSps(data.sps || []);
          setUnits(data.units || []);
          setLeases(data.leases || []);
          if (data.plan) setPlan(data.plan);
          if (data.userName) setUserName(data.userName);
          if (data.orgName) setOrgName(data.orgName);
          if (data.orgRole) setOrgRole(data.orgRole);
        } else if (!localStorage.getItem('auth_token')) {
          // Sync returned null due to 401 — force logout
          setLoggedIn(false); setRole(null); setCurrUserId(null);
        }
        const setts = await api.getSettings();
        if (setts && Object.keys(setts).length > 0) setSettings(prev => ({ ...prev, ...setts }));
      } catch (e) {
        console.error("Failed to fetch data or settings", e);
      } finally {
        setIsSyncing(false);
      }
    };
    syncData();
  }, [loggedIn]);

  // Poll for new data (notifications, messages, etc.) every 30 seconds — skip for admin
  // Refs so the interval closure always sees the latest role/currUserId/flash
  // without having to tear down + restart the setInterval on every state change.
  const roleRef = useRef(role); useEffect(() => { roleRef.current = role; }, [role]);
  const currUserIdRef = useRef(currUserId); useEffect(() => { currUserIdRef.current = currUserId; }, [currUserId]);
  const flashRef = useRef(null);
  useEffect(() => {
    if (role === 'admin' || role === 'user') return;
    const interval = setInterval(async () => {
      try {
        const data = await api.sync();
        if (data && !data.dbError) {
          setMsgs(data.msgs || []);
          setNotifs(prev => {
            const server = normNotifs(data.notifs);
            const localOnly = prev.filter(p => !server.find(s => s.id === p.id));
            // Detect newly arrived unread notifications targeted at the current
            // user so we can surface a toast — the bell badge alone is easy to miss
            // (esp. for landlords while they're on another tab/page).
            const r = roleRef.current;
            const uid = currUserIdRef.current;
            const isMine = (n) => {
              if (r === 'landlord') return n.forRole === 'landlord';
              if (r === 'tenant')   return n.forRole === 'tenant'  && n.forTid === uid;
              if (r === 'service')  return n.forRole === 'service' && n.forTid === uid;
              if (r === 'admin')    return n.forRole === 'admin';
              return false;
            };
            const fresh = server.filter(s => !s.read && isMine(s) && !prev.find(p => p.id === s.id));
            if (fresh.length > 0 && flashRef.current) {
              const msg = fresh.length === 1
                ? `🔔 ${fresh[0].msg}`
                : `🔔 ${fresh.length} new notifications`;
              flashRef.current(msg, "info");
            }
            return [...localOnly, ...server];
          });
          setMts(data.maints || []);
          setPays(data.pays || []);
          setCcs(data.ccs || []);
          setLeases(data.leases || []);
        }
      } catch (e) { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper Wrappers for API Persistence
  const updateProps = (newProps) => { setProps(newProps); newProps.forEach(p => api.saveProperty(p)); };
  const updatePays = (newPays) => { setPays(newPays); newPays.forEach(p => api.savePayment(p)); };

  const go = (p) => { window.scrollTo(0, 0); setPage(p); window.history.pushState({ page: p }, "", `#${p}`); };

  useEffect(() => {
    const handlePopState = (e) => { setPage(e.state?.page || "dash"); };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // FIX #6: Use functional updater to avoid stale closure
  const addNotif = (tRole, tId, msg, type) => {
    const newNotif = { id: `n${Date.now()}`, forRole: tRole, forTid: tId, msg, type, time: new Date().toLocaleTimeString(), read: false };
    setNotifs(prev => [newNotif, ...prev]);
    api.saveNotification(newNotif);
  };

  // FIX #21: Use toast-style notification instead of blocking alert()
  const flashTimerRef = useRef(null);
  const flash = (msg, type = "success") => {
    setNotif({ m: msg, type });
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setNotif(null), 3000);
  };
  // Expose `flash` to the polling effect (defined earlier via forward ref)
  flashRef.current = flash;

  // Reusable confirmation modal state (replaces window.confirm)
  const [confirmAction, setConfirmAction] = useState(null);
  const showConfirm = (message, onConfirm) => setConfirmAction({ message, onConfirm });
  const closeConfirm = () => setConfirmAction(null);

  // Re-sync helper for after mutations
  const refreshData = async () => {
    try {
      const data = await api.sync();
      if (data && !data.dbError) {
        setProps(data.props || []); setTens(data.tens || []); setPays(data.pays || []);
        setMts(data.maints || []); setCcs(data.ccs || []); setMsgs(data.msgs || []);
        setNotifs(normNotifs(data.notifs)); setSps(data.sps || []); setLeases(data.leases || []);
        if (data.plan) setPlan(data.plan);
        if (data.orgName) setOrgName(data.orgName);
      }
    } catch (e) { console.error("Refresh failed:", e); }
  };

  const doLogout = () => {
    localStorage.removeItem("loggedIn");
    localStorage.removeItem("role");
    localStorage.removeItem("currUserId");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("googleUser");
    localStorage.removeItem("orgId");
    api.logout();
    setLoggedIn(false);
    setRole(null);
    setCurrUserId(null);
    setOrgId(null);
    // Clear all user data on logout — prevents any stale data leaking to next session
    setNotifs([]);
    setProps([]);
    setTens([]);
    setPays([]);
    setMts([]);
    setCcs([]);
    setMsgs([]);
    setSps([]);
    setLeases([]);
    setIsSyncing(false); // Reset so next login doesn't get stuck
    go("dash");
  };

  // Pull-to-refresh
  const { handlers: pullHandlers, pulling, pullY } = usePullToRefresh(async () => {
    await refreshData();
    flash(T[lang].refreshed);
  });

  const ctxVal = { t: T[lang], h: dk ? themes.dark : themes.light, lang, setLang, dk, setDk, role, setRole, loggedIn, setLoggedIn, page, go, props, setProps, tens, setTens, pays, setPays, mts, setMts, ccs, setCcs, msgs, setMsgs, notifs, setNotifs, currUserId, setCurrUserId, selPropId, setSelPropId, selTid, setSelTid, openChat, setOpenChat, flash, addNotif, settings, setSettings, editingCC, setEditingCC, doLogout, showConfirm, closeConfirm, sps, setSps, units, setUnits, selMaintId, setSelMaintId, leases, setLeases, selLeaseId, setSelLeaseId, refreshData, plan, selOrgId, setSelOrgId, setPublicPage, orgId, setOrgId, userName, orgName, orgRole };

  if (dbErr) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f9ff", padding: "20px", textAlign: "center", flexDirection: "column" }}>
        <div style={{ background: "#fee2e2", padding: "30px", borderRadius: "16px", border: "2px solid #ef4444", maxWidth: "400px", boxShadow: "0 10px 25px rgba(239, 68, 68, 0.2)" }}>
          <h2 style={{ color: "#991b1b", marginTop: 0, fontSize: "22px" }}>{"⚠️ " + T[lang].dbConnError}</h2>
          <p style={{ color: "#7f1d1d", fontSize: "14px", fontWeight: "500", lineHeight: "1.5" }}>{T[lang].dbConnDesc}</p>
          <code style={{ display: "block", background: "#fef2f2", padding: "12px", borderRadius: "8px", color: "#b91c1c", marginTop: "15px", fontSize: "12px", wordWrap: "break-word", border: "1px dashed #fca5a5" }}>{dbErr}</code>
          <button onClick={() => window.location.reload()} style={{ marginTop: "25px", width: "100%", padding: "14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "15px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px", transition: "0.2s" }}>{T[lang].refreshConn}</button>
        </div>
      </div>
    );
  }

  if (!loggedIn && publicPage === "browse") return <Ctx.Provider value={ctxVal}><PublicBrowsePage onBack={() => setPublicPage(null)} /></Ctx.Provider>;
  if (!loggedIn) return <Ctx.Provider value={ctxVal}><LoginPage /></Ctx.Provider>;

  // Social auth users (role="user") → browse with profile context, no sync needed
  if (role === "user") return <Ctx.Provider value={ctxVal}><PublicBrowsePage onBack={doLogout} isLoggedIn={true} /></Ctx.Provider>;

  if (loggedIn && isSyncing) {
    return (
      <Ctx.Provider value={ctxVal}>
        <div style={{ background: ctxVal.h.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: 24, textAlign: "center" }}>
           <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke={ctxVal.h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite", marginBottom: 20 }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
           <h2 style={{ color: ctxVal.h.tx, margin: "0 0 8px", fontSize: 20 }}>{ctxVal.t.syncingDb}</h2>
           <p style={{ color: ctxVal.h.sc, fontSize: 14, maxWidth: 300, lineHeight: 1.5 }}>{ctxVal.t.wakingServer}<br/>{ctxVal.t.serverSleep} <b style={{color: ctxVal.h.wn}}>50 {ctxVal.t.seconds}</b> {ctxVal.t.initially}</p>
        </div>
      </Ctx.Provider>
    );
  }

  return <Ctx.Provider value={ctxVal}><div {...pullHandlers} style={{ background: ctxVal.h.bg, minHeight: "100vh", maxWidth: 430, margin: "0 auto", fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif", position: "relative" }}>
    {pulling && <div style={{ textAlign: "center", padding: `${Math.min(pullY / 2, 30)}px 0 8px`, transition: pulling ? "none" : "all 0.3s", overflow: "hidden" }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ctxVal.h.ac} strokeWidth="2" style={{ animation: pullY > 50 ? "spin 1s linear infinite" : "none", opacity: pullY / 80 }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div>}
    <TopBar /><div style={{ padding: "0 12px 74px" }}>{role === "landlord" ? <LandlordPage /> : role === "admin" ? <AdminPage /> : role === "service" ? <ServicePage /> : <TenantPage />}</div><BottomBar /><FeedbackButton />{notif && <Noti m={notif.m} type={notif.type} h={ctxVal.h} />}
    {confirmAction && <ConfirmModal h={ctxVal.h} t={ctxVal.t} message={confirmAction.message} onConfirm={() => { confirmAction.onConfirm(); closeConfirm(); }} onCancel={closeConfirm} />}
  </div></Ctx.Provider>;
}
